import { KEY_LABELS, NOTE_NAMES } from '../input/keyboard';
import { ink, newSeed, roundedRect, sketch } from './sketch';

const WHITE = [0, 2, 4, 5, 7, 9, 11];
const BLACK: { pc: number; after: number }[] = [
  { pc: 1, after: 0 },
  { pc: 3, after: 1 },
  { pc: 6, after: 3 },
  { pc: 8, after: 4 },
  { pc: 10, after: 5 },
];

interface Handlers {
  /** Called with a pitch class 0..11; the caller applies the octave. */
  down(pc: number): number;
  up(note: number): void;
}

/**
 * The twelve sketched keys. Lighting is by pitch class and reference
 * counted, so a MIDI note in any octave lights the matching key.
 */
export class KeysView {
  private keys: HTMLElement[] = [];
  private counts = new Array<number>(12).fill(0);

  constructor(private root: HTMLElement, private h: Handlers) {
    for (const pc of WHITE) this.keys[pc] = this.makeKey(pc, false);
    root.append(...WHITE.map((pc) => this.keys[pc]));
    for (const { pc, after } of BLACK) {
      const k = this.makeKey(pc, true);
      k.style.setProperty('--pos', String(after + 1));
      this.keys[pc] = k;
      root.append(k);
    }
    this.bindPointer();
  }

  press(note: number, color?: string): void {
    const pc = note % 12;
    this.counts[pc]++;
    const k = this.keys[pc];
    if (color) k.style.setProperty('--press', color);
    else k.style.removeProperty('--press');
    k.classList.add('is-down');
  }

  release(note: number): void {
    const pc = note % 12;
    this.counts[pc] = Math.max(0, this.counts[pc] - 1);
    if (!this.counts[pc]) this.keys[pc].classList.remove('is-down');
  }

  clear(): void {
    this.counts.fill(0);
    this.keys.forEach((k) => k.classList.remove('is-down'));
  }

  private makeKey(pc: number, black: boolean): HTMLElement {
    const k = document.createElement('div');
    k.className = `key ${black ? 'key--black' : 'key--white'}`;
    k.dataset.pc = String(pc);
    k.setAttribute('aria-label', NOTE_NAMES[pc]);
    k.innerHTML = `<span class="key__label"><b>${KEY_LABELS[pc]}</b><small>${NOTE_NAMES[pc]}</small></span>`;
    const seed = newSeed();
    const shape = (w: number, h: number) => roundedRect(2, -6, w - 4, h + 4, black ? 5 : 7);
    sketch(k, (rc, w, h) => [
      rc.path(
        shape(w, h),
        ink(
          black
            ? { seed, fill: 'currentColor', fillStyle: 'hachure', hachureGap: 2.6, fillWeight: 1.1, hachureAngle: -60 }
            : { seed },
        ),
      ),
    ], 'key__outline');
    sketch(k, (rc, w, h) => [
      rc.path(
        shape(w, h),
        ink({ seed: seed + 1000, stroke: 'none', fill: 'currentColor', fillStyle: black ? 'cross-hatch' : 'hachure', hachureGap: black ? 3 : 6.5, fillWeight: black ? 1.2 : 1, hachureAngle: 45 }),
      ),
    ], 'key__fill');
    return k;
  }

  private bindPointer(): void {
    // pointerId -> { pc, note } for each finger or mouse currently on a key.
    const active = new Map<number, { pc: number; note: number }>();
    const keyAt = (x: number, y: number): number | null => {
      const el = document.elementFromPoint(x, y)?.closest<HTMLElement>('.key');
      return el && this.root.contains(el) ? Number(el.dataset.pc) : null;
    };
    const start = (id: number, pc: number) => {
      active.set(id, { pc, note: this.h.down(pc) });
    };
    const stop = (id: number) => {
      const a = active.get(id);
      if (!a) return;
      this.h.up(a.note);
      active.delete(id);
    };
    this.root.addEventListener('pointerdown', (e) => {
      const pc = keyAt(e.clientX, e.clientY);
      if (pc === null) return;
      e.preventDefault();
      this.root.setPointerCapture(e.pointerId);
      start(e.pointerId, pc);
    });
    // Sliding across the keys plays a glissando.
    this.root.addEventListener('pointermove', (e) => {
      const a = active.get(e.pointerId);
      if (!a) return;
      const pc = keyAt(e.clientX, e.clientY);
      if (pc === a.pc) return;
      stop(e.pointerId);
      if (pc !== null) start(e.pointerId, pc);
      else active.set(e.pointerId, { pc: -1, note: -1 });
    });
    const end = (e: PointerEvent) => {
      const a = active.get(e.pointerId);
      if (a && a.note >= 0) this.h.up(a.note);
      active.delete(e.pointerId);
    };
    this.root.addEventListener('pointerup', end);
    this.root.addEventListener('pointercancel', end);
    this.root.addEventListener('lostpointercapture', end);
  }
}

/** Pitch bend springs back to center; mod stays where you leave it. */
export function bindWheel(
  el: HTMLElement,
  opts: { spring: boolean; onChange: (v: number) => void },
): { set(v: number): void } {
  const thumb = el.querySelector<HTMLElement>('.wheel__thumb')!;
  const seed = newSeed();
  sketch(thumb, (rc, w, h) => [rc.path(roundedRect(1, 1, w - 2, h - 2, 3), ink({ seed, fill: 'currentColor', fillStyle: 'solid', strokeWidth: 1 }))]);
  // spring wheels range -1..1 (center 0), others 0..1 (bottom 0).
  let value = 0;
  const render = () => {
    const frac = opts.spring ? (value + 1) / 2 : value;
    el.style.setProperty('--frac', String(frac));
    el.setAttribute('aria-valuenow', String(Math.round(value * 100)));
  };
  const set = (v: number, emit = true) => {
    value = opts.spring ? Math.max(-1, Math.min(1, v)) : Math.max(0, Math.min(1, v));
    render();
    if (emit) opts.onChange(value);
  };
  const fromPointer = (e: PointerEvent) => {
    const r = el.getBoundingClientRect();
    const frac = 1 - (e.clientY - r.top) / r.height;
    set(opts.spring ? frac * 2 - 1 : frac);
  };
  el.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    el.setPointerCapture(e.pointerId);
    el.classList.add('is-dragging');
    fromPointer(e);
  });
  el.addEventListener('pointermove', (e) => el.hasPointerCapture(e.pointerId) && fromPointer(e));
  const end = () => {
    el.classList.remove('is-dragging');
    if (opts.spring) set(0);
  };
  el.addEventListener('pointerup', end);
  el.addEventListener('pointercancel', end);
  el.addEventListener('keydown', (e) => {
    const d = e.key === 'ArrowUp' ? 0.1 : e.key === 'ArrowDown' ? -0.1 : 0;
    if (!d) return;
    e.preventDefault();
    e.stopPropagation();
    set(value + d);
  });
  el.addEventListener('keyup', (e) => {
    if (opts.spring && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) set(0);
  });
  render();
  return { set: (v) => set(v, false) };
}
